<template>
  <!-- Edit comment form -->
  <v-card v-if="comment.id" class="mb-4" variant="outlined">
    <v-card-title
      class="d-flex justify-space-between align-center py-2 px-4 bg-blue-lighten-5"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2" color="primary">mdi-pencil</v-icon>
        <span class="text-subtitle-1">Reactie wijzigen</span>
      </div>
      <v-btn
        icon="mdi-close"
        variant="text"
        size="small"
        color="grey"
        @click="resetComment"
      />
    </v-card-title>
    <v-card-text class="pa-4">
      <AtMentionBox
        v-model="comment.content"
        :instance_id="instance_id"
        :quick_links="'sd'"
      />
      <v-btn
        color="primary"
        class="mt-4"
        block
        :loading="loading"
        @click="submit"
      >
        <v-icon start>mdi-content-save</v-icon>
        Reactie bijwerken
      </v-btn>
    </v-card-text>
  </v-card>

  <!-- New comment form -->
  <div v-else>
    <v-sheet class="mb-2 px-1">
      <AtMentionBox
        v-model="comment.content"
        :instance_id="instance_id"
        :quick_links="'sd'"
      />
    </v-sheet>
    <v-btn
      color="primary"
      variant="elevated"
      block
      :loading="loading"
      :disabled="!comment.content.trim()"
      @click="submit"
    >
      <v-icon start>mdi-send</v-icon>
      Reactie plaatsen
    </v-btn>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import AtMentionBox from "../ui/AtMentionBox.vue";
import { useCommentsStoreRefactor } from "@/stores/commentsStoreRefactor";
import { useFormsStore } from "@/stores/formsStore";
import { useUsersStoreRefactor } from "@/stores/usersStoreRefactor";
import { useAlertStore } from "@/stores/alertStore";

export default {
  name: "CommentForm",
  components: {
    AtMentionBox,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    id: {
      type: [Number, String],
      required: true,
    },
    comment_edit: {
      type: Object,
      default: null,
    },
  },
  emits: ["commentReset", "comment-created", "comment-updated"],
  setup(props, { emit }) {
    const commentsStore = useCommentsStoreRefactor();
    const formsStore = useFormsStore();
    const usersStore = useUsersStoreRefactor();
    const alertStore = useAlertStore();
    const comment = ref({
      id: "",
      content: "",
      commentable_type: props.type,
      commentable_id: props.id,
    });
    const instance_id = ref(1);
    const loading = ref(false);

    const resetComment = () => {
      comment.value = {
        id: "",
        content: "",
        commentable_type: props.type,
        commentable_id: props.id,
      };
      emit("commentReset");
    };

    const actionLabel = computed(() => {
      return comment.value.id ? "wijzigen" : "toevoegen";
    });

    const updateCommentFromEdit = (newComment) => {
      if (newComment && typeof newComment === "object") {
        comment.value = {
          id: newComment.id || "",
          content: newComment.content || "",
          commentable_type: props.type,
          commentable_id: props.id,
        };
      } else {
        resetComment();
      }
    };

    // Watch for changes to comment_edit prop
    watch(
      () => props.comment_edit,
      (newVal) => {
        updateCommentFromEdit(newVal);
      },
      { immediate: true },
    );

    const submit = async () => {
      if (!comment.value.content.trim()) {
        return;
      }

      loading.value = true;
      try {
        if (comment.value.id) {
          // Update existing comment
          const updatedComment = await commentsStore.update(comment.value);
          console.log(
            "CommentForm: Comment updated successfully:",
            updatedComment,
          );

          // Show success message
          alertStore.success("Reactie succesvol bijgewerkt");

          // Emit event
          emit("comment-updated", updatedComment);

          // Reset form
          resetComment();

          // Reset form in store
          formsStore.$reset();
        } else {
          // Create new comment
          console.log(
            "CommentForm: Creating new comment for",
            comment.value.commentable_type,
            comment.value.commentable_id,
          );
          const newComment = await commentsStore.create(comment.value);
          console.log("CommentForm: Comment created successfully:", newComment);

          if (!newComment || !newComment.id) {
            console.error("CommentForm: Invalid comment response:", newComment);
            throw new Error(
              "Invalid response from server - no comment data returned",
            );
          }

          // Show success message (and ensure we only do this for successful responses)
          alertStore.success("Reactie succesvol geplaatst");

          // The store's create method already added the comment via _upsertItem override
          // No need to manually add it again

          // Emit event
          console.log("CommentForm: Emitting comment-created event");
          emit("comment-created", newComment);

          // Reset form content but keep the form open
          comment.value.content = "";

          // Don't reset the form in the store for comments
          // This keeps the form open
        }
      } catch (error) {
        console.error("Error submitting comment:", error);

        // Show appropriate error message
        if (error.response && error.response.data) {
          // API returned an error response
          const errorMsg =
            error.response.data.error ||
            error.response.data.message ||
            "Fout bij het opslaan van de reactie";
          console.error("CommentForm: Server returned error:", errorMsg);
          alertStore.error(errorMsg);
        } else {
          // Network or other error
          const errorMessage =
            "Fout bij het opslaan van de reactie: " +
            (error.message || "Onbekende fout");
          console.error("CommentForm: Client-side error:", errorMessage);
          alertStore.error(errorMessage);
        }
      } finally {
        loading.value = false;
      }
    };

    onMounted(async () => {
      try {
        await usersStore.getAll();
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    });

    return {
      comment,
      instance_id,
      loading,
      actionLabel,
      resetComment,
      submit,
    };
  },
};
</script>
